export type Pricing = {
  markup: number;
  discount: number;
};

export enum PricingType {
  HOTELS = "hotels",
  FLIGHTS = "flights",
}

export type TenantInfo = {
  id: string;
};

export type SocialLinks = {
  twitter?: string;
  facebook?: string;
  telegram?: string;
  instagram?: string;
  linkedin?: string;
  discord?: string;
  youtube?: string;
};

export type LocalizedText = {
  en: string;
  "zh-CN": string;
  "zh-TW": string;
};

export type BannerSettings = {
  imageUrl: string;
  title: LocalizedText;
  description: LocalizedText;
};

export type FAQ = {
  id: string;
  question: LocalizedText;
  answer: LocalizedText;
  order: number;
};

/** One card in the "Enhance your travel experience" section of the home page. */
export type TravelFeature = {
  id: string;
  title: LocalizedText;
  description: LocalizedText;
  order: number;
  /** 6-digit hex. Blank falls back to the palette colour for this position. */
  bgColor: string;
  /** 6-digit hex. Blank falls back to the palette colour for this position. */
  textColor: string;
  /** data: URI or https URL. Blank falls back to the built-in icon for this position. */
  iconUrl: string;
};

export type TravelExperience = {
  heading: LocalizedText;
  features: TravelFeature[];
};

/** Four cards fill the row on a wide screen; beyond that the row scrolls. */
export const MAX_TRAVEL_FEATURES = 8;

/**
 * Icons are stored inline in the branding JSON that every public page carries,
 * so they have to stay small. An optimised SVG is 1-3 KB.
 */
export const MAX_TRAVEL_FEATURE_ICON_BYTES = 16 * 1024;

export type PolicyContent = {
  privacyPolicy: LocalizedText;
  termsConditions: LocalizedText;
};

export type FooterSettings = {
  text: LocalizedText;
};

export const ALL_PAYMENT_METHOD_IDS = [
  "crypto",
  "credit",
  "applepay",
  "klarna",
  "wechat",
  "alipay",
] as const;

export type PaymentMethodId = (typeof ALL_PAYMENT_METHOD_IDS)[number];

export type TenantCurrency = {
  label: string;
  value: string;
  name: string;
  order: number;
  type: "FIAT" | "CRYPTO" | null;
};

/**
 * `beforeInteractive` is deliberately absent: it blocks first paint and must
 * live in the root layout, so a tenant could stall their own storefront with it.
 */
export const TENANT_SCRIPT_STRATEGIES = [
  "afterInteractive",
  "lazyOnload",
] as const;

export type TenantScriptStrategy = (typeof TENANT_SCRIPT_STRATEGIES)[number];

/** A third-party tag a tenant loads on their public pages, such as a chat widget. */
export type TenantScript = {
  id: string;
  /** Dashboard-only label, so an admin can tell two tags apart. */
  name: string;
  /** Absolute https URL of the script file. */
  src: string;
  strategy: TenantScriptStrategy;
  /** `data-*` pairs the vendor asks for, such as a widget's public key. */
  attributes: Record<string, string>;
  enabled: boolean;
};

export const MAX_TENANT_SCRIPTS = 10;
export const MAX_TENANT_SCRIPT_ATTRIBUTES = 10;
export const MAX_TENANT_SCRIPT_ATTRIBUTE_VALUE_LENGTH = 512;
export const MAX_TENANT_SCRIPT_NAME_LENGTH = 80;
export const MAX_TENANT_SCRIPT_SRC_LENGTH = 2048;

export type TenantBranding = {
  name: string;
  logoUrl: string;
  socialLinks: SocialLinks;
  showEvents: boolean;
  showFlights: boolean;
  testMode: boolean;
  supportedCurrencies: string[] | null;
  availableCurrencies?: TenantCurrency[];
  banner: BannerSettings;
  flightBanner: BannerSettings;
  faqs: FAQ[];
  travelExperience: TravelExperience;
  customScripts: TenantScript[];
  policies: PolicyContent;
  footer: FooterSettings;
  paymentMethods: PaymentMethodId[];
  contactPhone: string;
  contactEmail: string;
  faviconUrl: string;
  copyright: string;
  emptyStateImageUrl: string;
  primaryColor: string;
  secondaryColor: string;
};

/**
 * Email delivery config is dashboard-only: it must never reach the public
 * branding context that is serialised to the browser.
 */
export type TenantEmailBranding = {
  emailFromAddress: string;
  emailSubjectPrefix: string;
  emailOperatorBrand: string;
};

export type TenantAdminBranding = TenantBranding &
  TenantEmailBranding & {
    /**
     * The platform cards, read from the translations. Dashboard-only: it gives
     * a tenant something to edit from rather than a blank editor.
     */
    defaultTravelExperience?: TravelExperience;
  };

export const EMPTY_LOCALIZED_TEXT: LocalizedText = {
  en: "",
  "zh-CN": "",
  "zh-TW": "",
};

export const applyLocalizedFallback = (
  text: Partial<LocalizedText>
): LocalizedText => {
  const english = text?.en ?? "";
  return {
    en: english,
    // || intentional: empty string falls back to English for CJK locales
    "zh-CN": text?.["zh-CN"] || english,
    "zh-TW": text?.["zh-TW"] || english,
  };
};

export const toTitleCase = (name: string): string =>
  name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .trim();

export const TENANT_ADMIN_ROLES = ["owner", "admin", "editor"] as const;

export type TenantAdminRole = (typeof TENANT_ADMIN_ROLES)[number];

export type AccessibleTenant = {
  id: string;
  name: string;
  /** Null for super admins, who reach every tenant without an assignment row. */
  role: TenantAdminRole | null;
};

export type TenantAdminEntry = {
  id: number;
  email: string;
  role: TenantAdminRole;
  assignedAt: string;
};

export type DashboardAccessResponse = {
  email: string;
  isSuperAdmin: boolean;
  hostTenantId: string | null;
  tenants: AccessibleTenant[];
};
